import { FlexBox, Icon, Text } from '@dixa/lib-shared-habla';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import {
  ArticleSavingContext,
  online,
} from 'articleEditor/contexts/savingContext';
import { useContext } from 'react';
import { useSnapshot } from 'valtio';

import { CloudCheck, CloudSlash, CloudSync } from './savingIndicator.icons';
import s from './savingIndicator.module.scss';

export function SavingIndicator(): JSX.Element | null {
  const { savingStore } = useContext(ArticleSavingContext);
  const { savingState } = useSnapshot(savingStore);
  const [parentRef] = useAutoAnimate<HTMLDivElement>({
    // duration: 800,
  });

  const saveState = !online ? 'offline' : savingState;
  if (!saveState) {
    return null;
  }

  return (
    <div ref={parentRef} className={s.location} data-id="this is me">
      <SavingIndicatorInner saveState={saveState} />
    </div>
  );
}

type SavingIndicatorInnerProps = {
  saveState: 'saving' | 'saved' | 'error' | 'offline';
};

export function SavingIndicatorInner({
  saveState,
}: SavingIndicatorInnerProps): JSX.Element {
  if (saveState === 'saving') {
    return <SaveIndicatorTemplate icon={<CloudSync />} text="Saving..." />;
  }
  if (saveState === 'saved') {
    return (
      <SaveIndicatorTemplate
        icon={<CloudCheck />}
        text="Saved"
        variant="success"
      />
    );
  }
  if (saveState === 'error') {
    return (
      <SaveIndicatorTemplate
        icon={<Icon name="exclamation-triangle-filled" />}
        text="Saving failed. Please try again."
        variant="error"
      />
    );
  }
  if (saveState === 'offline') {
    return (
      <SaveIndicatorTemplate
        icon={<CloudSlash />}
        text="You are offline. Changes will be saved when you reconnect."
        variant="warn"
      />
    );
  }

  return <></>;
}

type SaveIndicatorTemplateProps = {
  icon: JSX.Element;
  text: string;
  variant?: 'success' | 'error' | 'info' | 'warn';
};

const indicatorVariants = {
  success: {
    backgroundColor: 'green.100',
    borderColor: 'green.300',
    textColor: 'green.600',
  },
  error: {
    backgroundColor: 'red.100',
    borderColor: 'red.300',
    textColor: 'red.600',
  },
  info: {
    backgroundColor: 'neutral.100',
    borderColor: 'neutral.300',
    textColor: 'neutral.600',
  },
  warn: {
    backgroundColor: 'orange.100',
    borderColor: 'orange.300',
    textColor: 'orange.600',
  },
};

function SaveIndicatorTemplate({
  text,
  icon,
  variant = 'info',
}: SaveIndicatorTemplateProps): JSX.Element {
  return (
    <FlexBox
      gap="200"
      background={indicatorVariants[variant].backgroundColor}
      padding="10px"
      borderRadius="5px"
      borderColor={indicatorVariants[variant].borderColor}
      alignItems="center"
    >
      <FlexBox
        className={s.iconWrapper}
        style={{ color: indicatorVariants[variant].textColor }}
      >
        {icon}
      </FlexBox>
      <Text.Paragraph variant="3" color={indicatorVariants[variant].textColor}>
        {text}
      </Text.Paragraph>
    </FlexBox>
  );
}
